import React, { useState, useRef, useEffect } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faUser, faCog, faSignOutAlt, faBook, faBars, faChevronDown, faUserShield, faBell } from '@fortawesome/free-solid-svg-icons'
import { useNavigate } from 'react-router-dom'
import SettingsModal from './SettingsModal'
import NotificationsPanel from './NotificationsPanel'
import KioskNavbar from './KioskNavbar'
import { useNotifications } from '../contexts/NotificationContext'
import { useAuth } from '../contexts/AuthContext'
import { canAccessTallyApp } from '../utils/tallyPermissions'
import { isKioskRole, useKioskRole } from '../hooks/useKioskRole'
import { useBodyScrollLock } from '../hooks/useBodyScrollLock'
import { useAnimatedPresence } from '../hooks/useAnimatedPresence'

/** Block the synthetic click that would hit page content after a mobile overlay unmounts. */
function suppressGhostClick(durationMs = 400) {
  const block = (event: Event) => {
    event.preventDefault()
    event.stopPropagation()
  }
  document.addEventListener('click', block, true)
  document.addEventListener('pointerup', block, true)
  window.setTimeout(() => {
    document.removeEventListener('click', block, true)
    document.removeEventListener('pointerup', block, true)
  }, durationMs)
}

interface NavbarProps {
  pageTitle?: string
  news?: string[]
  recentActivity?: string[]
}

const PAGES = [
  { name: 'DASHBOARD', label: 'Dashboard', path: '/', permissionKey: null },
  { name: 'FORMS', label: 'Forms', path: '/forms', permissionKey: 'forms' },
  { name: 'PROJECTS', label: 'Projects', path: '/projects', permissionKey: 'projects' },
  { name: 'RESOURCES', label: 'Resources', path: '/resources', permissionKey: 'resources' },
  { name: 'CONTROL_CENTER', label: 'Control Center', path: '/control-center', permissionKey: 'controlCenter' },
  { name: 'TALLY', label: 'Tally', path: '/tally', permissionKey: 'tally' },
  { name: 'ADMIN', label: 'Admin', path: '/admin', permissionKey: 'adminPage' },
  { name: 'HR', label: 'HR', path: '/hr', permissionKey: 'hrManager' },
  { name: 'PROFILE', label: 'Profile', path: '/profile', permissionKey: 'profilePage' },
  { name: 'TEMPLATE', label: 'Template', path: '/template', permissionKey: 'template' },
  { name: 'PR', label: 'PR', path: '/pr', permissionKey: 'pr' },
]

const Navbar: React.FC<NavbarProps> = ({ pageTitle = 'PORTAL' }) => {
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [drawerView, setDrawerView] = useState<'menu' | 'notifications'>('menu')
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [notifOpen, setNotifOpen] = useState(false)
  const navigate = useNavigate()
  const avatarButtonRef = useRef<HTMLDivElement>(null)
  const notifMenuRef = useRef<HTMLDivElement>(null)
  const { unreadCount } = useNotifications()
  const { logout, user, hasPermission } = useAuth()
  const hasAnyTallyPermission = canAccessTallyApp(hasPermission)
  const kioskRole = useKioskRole()

  useEffect(() => {
    if (!notifOpen) return
    const onPointerDown = (event: PointerEvent) => {
      const root = notifMenuRef.current
      if (root && !root.contains(event.target as Node)) {
        setNotifOpen(false)
      }
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setNotifOpen(false)
    }
    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [notifOpen])

  const drawerPresence = useAnimatedPresence(drawerOpen, 280)
  const notifPresence = useAnimatedPresence(notifOpen, 180)

  useBodyScrollLock(drawerPresence.mounted)

  useEffect(() => {
    if (!drawerPresence.mounted) setDrawerView('menu')
  }, [drawerPresence.mounted])

  // Display: no chrome. Controller / regular: dedicated kiosk bar.
  if (kioskRole === 'display') return null
  if (isKioskRole(kioskRole)) return <KioskNavbar />

  const openDrawer = (view: 'menu' | 'notifications' = 'menu') => {
    setDrawerView(view)
    setDrawerOpen(true)
  }

  const closeDrawer = () => {
    if (drawerOpen) suppressGhostClick()
    setDrawerOpen(false)
  }

  const handleLogout = () => {
    logout()
  }

  const handleResources = () => {
    navigate('/resources')
    closeDrawer()
  }

  const handleNavigate = (pageName: string) => {
    const page = PAGES.find(p => p.name === pageName)
    if (page) {
      navigate(page.path)
    }
  }

  const accessiblePages = PAGES.filter(page => {
    if (page.permissionKey === null) return true
    if (page.name === 'TALLY') return hasAnyTallyPermission
    return hasPermission(page.permissionKey)
  })

  return (
    <>
      <SettingsModal open={settingsOpen} onClose={() => setSettingsOpen(false)} />
      {/* Desktop Navbar */}
      <div className="navbar bg-base-100 shadow-lg border-b border-base-300 px-6 hidden sm:flex relative">
        <div className="navbar-start">
          <div className="flex items-center gap-0.5">
            <span className="text-base-content font-bold text-xl">LTS</span>
            <div className="dropdown dropdown-bottom">
              <div tabIndex={0} className="btn btn-ghost btn-sm px-1 font-bold text-xl flex items-center text-primary ltsPrimaryText hover:bg-base-200 focus:bg-base-200">
                {pageTitle}
                <FontAwesomeIcon icon={faChevronDown} className="ml-1 text-base" />
              </div>
              <ul tabIndex={0} className="menu menu-sm dropdown-content mt-2 z-[2] p-2 shadow bg-base-100 rounded-box w-48">
                {accessiblePages.map((page) => (
                  <li key={page.name}>
                    <button
                      className={`flex items-center gap-2 ${
                        page.name === pageTitle
                          ? 'text-primary font-semibold ltsPrimaryText'
                          : 'text-base-content'
                      }`}
                      onClick={() => handleNavigate(page.name)}
                    >
                      {page.label}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
        <div className="navbar-center absolute left-0 right-0 flex justify-center pointer-events-none">
          <div className="text-center pointer-events-auto">
            <span className="text-base-content font-medium">Welcome, <span className="text-primary font-bold welcomeUserName">{user?.displayName || 'User'}</span></span>
          </div>
        </div>
        <div className="navbar-end flex items-center gap-1">
          <div className="relative" ref={notifMenuRef}>
            <button
              type="button"
              className="btn btn-ghost btn-circle overflow-visible"
              aria-label="Notifications"
              aria-expanded={notifOpen}
              aria-haspopup="true"
              onClick={() => setNotifOpen((v) => !v)}
            >
              <div className="indicator">
                <FontAwesomeIcon icon={faBell} className="text-xl" />
                {unreadCount > 0 && (
                  <span className="badge badge-xs badge-error indicator-item">
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
              </div>
            </button>
            {notifPresence.mounted && (
              <div
                className={`portal-menu-pop absolute right-0 z-[3] mt-3 w-96 max-w-[calc(100vw-2rem)] overflow-hidden rounded-box border border-base-300 bg-base-100 shadow-lg ${
                  notifPresence.visible ? 'is-open' : ''
                }`}
              >
                <NotificationsPanel
                  onClose={() => setNotifOpen(false)}
                  onItemActivate={() => setNotifOpen(false)}
                  maxItems={20}
                  listClassName="max-h-96"
                />
              </div>
            )}
          </div>
          <div className="dropdown dropdown-end">
            <div tabIndex={0} role="button" className="btn btn-ghost btn-circle avatar" ref={avatarButtonRef}>
              <div className="w-10 h-10 rounded-full bg-primary ltsPrimaryBg text-white flex items-center justify-center">
                <FontAwesomeIcon icon={faUser} className="text-2xl" style={{ marginTop: '6px' }} />
              </div>
            </div>
            <ul tabIndex={0} className="menu menu-sm dropdown-content mt-3 z-[1] p-2 shadow bg-base-100 rounded-box w-52">
              {hasPermission('resources') && (
                <li>
                  <button
                    onClick={handleResources}
                    className="flex items-center gap-2 text-base-content"
                  >
                    <FontAwesomeIcon icon={faBook} />
                    Resources
                  </button>
                </li>
              )}
              <li>
                <button
                  onClick={() => {
                    if (document.activeElement instanceof HTMLElement) {
                      document.activeElement.blur()
                    }
                    setSettingsOpen(true)
                  }}
                  className="flex items-center gap-2 text-base-content"
                >
                  <FontAwesomeIcon icon={faCog} />
                  Settings
                </button>
              </li>
              {hasPermission('profilePage') && (
                <li>
                  <button
                    onClick={() => handleNavigate('PROFILE')}
                    className="flex items-center gap-2 text-base-content"
                  >
                    <FontAwesomeIcon icon={faUser} />
                    Profile
                  </button>
                </li>
              )}
              <li>
                <button
                  onClick={handleLogout}
                  className="flex items-center gap-2 text-error"
                >
                  <FontAwesomeIcon icon={faSignOutAlt} />
                  Log Out
                </button>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Mobile Navbar — fixed so it stays put on h-screen overflow-hidden pages (CC, Tally, Resources) */}
      <div
        className="navbar invisible pointer-events-none shrink-0 px-4 pt-safe sm:hidden"
        aria-hidden
      >
        <div className="flex-1" />
        <div className="btn btn-ghost btn-circle btn-lg" />
        <div className="btn btn-ghost btn-circle btn-lg" />
      </div>
      <div className="navbar fixed inset-x-0 top-0 z-40 bg-base-100 shadow-lg border-b border-base-300 px-4 pt-safe flex sm:hidden">
        <div className="flex-1 flex justify-start min-w-0">
          <div className="flex items-center gap-0.5">
            <span className="text-base-content font-bold text-xl">LTS</span>
            <div className="dropdown dropdown-bottom">
              <div tabIndex={0} className="btn btn-ghost btn-sm min-h-11 px-1 font-bold text-xl flex items-center text-primary ltsPrimaryText hover:bg-base-200 focus:bg-base-200">
                {pageTitle}
                <FontAwesomeIcon icon={faChevronDown} className="ml-1 text-base" />
              </div>
              <ul tabIndex={0} className="menu menu-lg dropdown-content mt-2 z-[2] p-2 shadow bg-base-100 rounded-box min-w-56 w-max">
                {accessiblePages.map((page) => (
                  <li key={page.name}>
                    <button
                      className={`min-h-12 text-base ${
                        page.name === pageTitle
                          ? 'text-primary font-semibold ltsPrimaryText'
                          : 'text-base-content'
                      }`}
                      onClick={() => handleNavigate(page.name)}
                    >
                      {page.label}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-0.5 ml-2">
          <button
            type="button"
            className="btn btn-ghost btn-circle btn-lg overflow-visible"
            aria-label="Notifications"
            onClick={() => openDrawer('notifications')}
          >
            <div className="indicator">
              <FontAwesomeIcon icon={faBell} className="text-xl text-base-content" />
              {unreadCount > 0 && (
                <span className="badge badge-xs badge-error indicator-item">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </div>
          </button>
          <button
            type="button"
            className="btn btn-ghost btn-circle btn-lg"
            aria-label="Open menu"
            onClick={() => openDrawer('menu')}
          >
            <FontAwesomeIcon icon={faBars} className="text-2xl text-base-content" />
          </button>
        </div>
      </div>

      {drawerPresence.mounted && (
        <div className="fixed inset-0 z-50 overflow-hidden" role="dialog" aria-modal="true">
        <div
          className={`portal-drawer flex h-full w-full flex-col bg-base-100 pt-safe ${
            drawerPresence.visible ? 'is-open' : ''
          }`}
        >
          <div
            className={`flex min-h-0 flex-1 flex-col ${
              drawerView === 'notifications' ? 'overflow-hidden' : 'overflow-y-auto'
            }`}
          >
            {drawerView === 'notifications' ? (
              <div className="flex h-full min-h-0 flex-1 flex-col">
                <NotificationsPanel
                  className="min-h-0 flex-1"
                  listClassName="min-h-0"
                  showClose={false}
                  maxItems={50}
                  onItemActivate={closeDrawer}
                />
                <div className="shrink-0 border-t border-base-200 pb-safe">
                  <button
                    type="button"
                    className="btn btn-ghost min-h-12 h-12 w-full rounded-none text-base-content"
                    onClick={closeDrawer}
                  >
                    Close
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div className="flex flex-col items-center py-6 border-b border-base-200">
                  <div className="w-16 h-16 rounded-full bg-primary ltsPrimaryBg text-white flex items-center justify-center mb-2">
                    <FontAwesomeIcon icon={faUser} className="text-3xl" style={{ marginTop: '8px' }} />
                  </div>
                  <span className="font-semibold text-lg text-base-content">Welcome, {user?.displayName || 'User'}</span>
                </div>
                <ul className="menu p-4 flex-1">
                  <li>
                    <button onClick={() => { closeDrawer(); navigate('/'); }} className="flex items-center gap-3 text-base text-base-content min-h-11">
                      <FontAwesomeIcon icon={faUserShield} />
                      Dashboard
                    </button>
                  </li>
                  {hasPermission('resources') && (
                    <li>
                      <button onClick={handleResources} className="flex items-center gap-3 text-base text-base-content min-h-11">
                        <FontAwesomeIcon icon={faBook} />
                        Resources
                      </button>
                    </li>
                  )}
                  <li>
                    <button
                      onClick={() => setDrawerView('notifications')}
                      className="flex items-center gap-3 text-base text-base-content min-h-11"
                    >
                      <span className="indicator">
                        <FontAwesomeIcon icon={faBell} />
                        {unreadCount > 0 && (
                          <span className="badge badge-xs badge-error indicator-item">
                            {unreadCount > 99 ? '99+' : unreadCount}
                          </span>
                        )}
                      </span>
                      Notifications
                    </button>
                  </li>
                  <li>
                    <button onClick={() => {
                      closeDrawer()
                      setTimeout(() => setSettingsOpen(true), 100)
                    }} className="flex items-center gap-3 text-base text-base-content min-h-11">
                      <FontAwesomeIcon icon={faCog} />
                      Settings
                    </button>
                  </li>
                  {hasPermission('profilePage') && (
                    <li>
                      <button onClick={() => { closeDrawer(); navigate('/profile'); }} className="flex items-center gap-3 text-base text-base-content min-h-11">
                        <FontAwesomeIcon icon={faUser} />
                        Profile
                      </button>
                    </li>
                  )}
                  <li>
                    <button onClick={handleLogout} className="flex items-center gap-3 text-base text-error min-h-11">
                      <FontAwesomeIcon icon={faSignOutAlt} />
                      Log Out
                    </button>
                  </li>
                </ul>
                <div className="shrink-0 border-t border-base-200 pb-safe">
                  <button
                    type="button"
                    className="btn btn-ghost min-h-12 h-12 w-full rounded-none text-base-content"
                    onClick={closeDrawer}
                  >
                    Close
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
        </div>
      )}
    </>
  )
}

export default Navbar
